import { type ReactNode } from "react";
import * as Sentry from "@sentry/react";

/**
 * Detects the "stale index.html asking for a purged chunk" failure.
 *
 * Hashed assets are served `immutable` while index.html is not, so after a
 * deploy a cached document can request a chunk that no longer exists. That
 * surfaces as a dynamic-import failure, and a generic fallback would strand the
 * user on a document that can never finish loading — a reload is the actual fix,
 * so we do it once automatically.
 */
function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

const RELOAD_FLAG = "chunk_reload_attempted";

/**
 * `sessionStorage` throws SecurityError when storage is blocked — Safari's
 * "block all cookies", locked-down enterprise profiles, and the in-app
 * webviews this app already targets. A throw inside a boundary's own fallback
 * is NOT caught by that boundary, so for the outermost one React would unmount
 * the root and render a blank page: precisely the failure this component
 * exists to prevent. Both accessors therefore fail safe.
 */
function readReloadFlag(): string | null {
  try {
    return sessionStorage.getItem(RELOAD_FLAG);
  } catch {
    // Treat as "already attempted" so we show the fallback UI instead of
    // risking a reload loop we cannot record.
    return "1";
  }
}

function writeReloadFlag(): boolean {
  try {
    sessionStorage.setItem(RELOAD_FLAG, "1");
    return true;
  } catch {
    return false;
  }
}

function clearReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

function ErrorFallback({ error }: { error: unknown }) {
  // Reload once for a stale-chunk failure. The flag guards against a reload
  // loop when the chunk is genuinely gone rather than merely cached — so we
  // only reload if we could actually record the attempt.
  if (isChunkLoadError(error) && !readReloadFlag() && writeReloadFlag()) {
    window.location.reload();
    return null;
  }

  return (
    <div
      role="alert"
      className="flex min-h-screen w-full flex-col items-center justify-center gap-4 px-6 text-center"
    >
      <h1 className="text-xl font-semibold text-zinc-950">
        Something went wrong
      </h1>
      <p className="max-w-md text-sm text-zinc-500">
        The page ran into an unexpected problem. Reloading usually fixes it. Your
        exam history is stored on your account and is not affected.
      </p>
      <button
        type="button"
        onClick={() => {
          clearReloadFlag();
          window.location.reload();
        }}
        className="rounded-lg bg-zinc-950 px-5 py-2.5 text-sm font-medium text-white"
      >
        Reload
      </button>
    </div>
  );
}

/**
 * Root error boundary.
 *
 * Without one, React 19 unmounts the entire root on any render or lifecycle
 * error, leaving a blank white page with no recovery path other than a manual
 * reload. `onCaughtError` in main.tsx can only fire when a boundary exists, so
 * before this every such error also took the uncaught path.
 *
 * Boundaries do not catch event-handler or async errors — those still need
 * their own handling at the call site.
 */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => <ErrorFallback error={error} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export default AppErrorBoundary;
